import { apiFetchRaw, apiRequest } from './client';

/**
 * `/api/backups` — export and restore the whole installation (V0.48).
 *
 * Its own mount rather than a branch of `/api/admin`, because the server gates that router on
 * the user-administration permission and this is a standalone role somebody can hold without
 * it. `BACKUPS_ROOT` is the one line that would move if a deployment ever decided otherwise.
 */

export const BACKUPS_ROOT = '/api/backups';

export type BackupKind = 'export' | 'safety' | 'uploaded';

/** Can this bundle be restored onto this server, and what will it cost? */
export type BackupCompat = 'same' | 'older-will-migrate' | 'newer-refused' | 'foreign-refused' | 'unreadable';

export interface BackupSummary {
  appVersion: string;
  exportedAt: string;
  exportedBy: string;
  tables: number;
  rows: number;
  uploadsFiles: number;
  uploadsBytes: number;
  /** Who will be able to sign in AFTER a restore. The most consequential line on the screen. */
  adminUsernames: string[];
}

export interface BackupEntry {
  name: string;
  kind: BackupKind;
  bytes: number;
  createdAt: string;
  encrypted: boolean;
  /** The six characters that must be typed to restore it. Null when it cannot be read. */
  code: string | null;
  compat: BackupCompat;
  pending: number;
  encKeyMatch: boolean | null;
  summary: BackupSummary | null;
  problem?: string;
}

export interface BackupJob {
  id: string;
  kind: 'export' | 'import';
  state: 'running' | 'done' | 'error';
  step: string;
  detail?: Record<string, unknown>;
  startedAt: string;
  finishedAt?: string;
  error?: string;
  resultName?: string;
  actor: string;
}

export interface InterruptedImport {
  bundle: string;
  actor: string;
  startedAt: string;
  step: string;
  table?: string;
  safetyCopy: string | null;
  error?: string;
  description: string;
}

export interface BackupsPayload {
  entries: BackupEntry[];
  job: BackupJob | null;
  interrupted: InterruptedImport | null;
  maintenance: { active: boolean; reason: string | null; since: string | null };
  localVersion: string;
  localMigrations: number;
  limits: { maxUploadMb: number };
}

export const fetchBackups = (): Promise<BackupsPayload> => apiRequest<BackupsPayload>(BACKUPS_ROOT);

/** A passphrase, when given, protects the finished bundle. It is never stored anywhere. */
export const startExport = (passphrase?: string): Promise<{ jobId: string }> =>
  apiRequest<{ jobId: string }>(`${BACKUPS_ROOT}/export`, {
    method: 'POST',
    body: passphrase ? { passphrase } : {},
  });

/**
 * Start a restore.
 *
 * A protected bundle sends its PASSPHRASE and no code: the code is derived from a manifest
 * nobody can read without one, and having produced the passphrase is a stronger statement of
 * intent than having retyped six characters that were on screen. The server enforces which of
 * the two it requires.
 */
export const startImport = (
  name: string,
  confirm: { confirmCode?: string; passphrase?: string },
): Promise<{ jobId: string; ticket: string }> =>
  apiRequest<{ jobId: string; ticket: string }>(`${BACKUPS_ROOT}/${encodeURIComponent(name)}/import`, {
    method: 'POST',
    body: confirm.passphrase ? { passphrase: confirm.passphrase } : { confirmCode: confirm.confirmCode },
  });

/**
 * One job's progress.
 *
 * The TICKET is what makes this work during a restore, and it is the reason this takes an
 * optional second argument at all: while the database is being replaced the `User` table is
 * gone for a moment, so the session cannot be resolved and a cookie-authenticated poll would
 * answer 401 and sign the admin out of the app in the middle of watching their own restore.
 * The server mounts this route twice for that reason; the ticket path consults no cookie.
 */
export const fetchBackupJob = (id: string, ticket?: string): Promise<BackupJob> =>
  apiRequest<BackupJob>(`${BACKUPS_ROOT}/jobs/${encodeURIComponent(id)}`, ticket ? { query: { ticket } } : {});

export const deleteBackup = (name: string): Promise<void> =>
  apiRequest<void>(`${BACKUPS_ROOT}/${encodeURIComponent(name)}`, { method: 'DELETE' });

export const dismissInterrupted = (): Promise<void> =>
  apiRequest<void>(`${BACKUPS_ROOT}/interrupted/dismiss`, { method: 'POST' });

/**
 * The href a plain `<a download>` points at.
 *
 * A pure function, and the download is a plain link on purpose: it is same-origin, so the
 * session cookie rides along, and `Content-Disposition: attachment` does the rest. No blob, no
 * object URL, nothing to revoke — and nothing for the offline spec's request watcher to
 * object to.
 */
export const backupDownloadUrl = (name: string): string =>
  `${BACKUPS_ROOT}/${encodeURIComponent(name)}/download`;

/**
 * Send a bundle from this machine.
 *
 * `apiFetchRaw` rather than `apiRequest`, for the reason `api/client.ts` gives at length: a
 * JSON content type strips the multipart boundary the server needs. The same shape the avatar
 * and doc-image uploads already use.
 */
export async function uploadBackup(file: File): Promise<BackupEntry> {
  const form = new FormData();
  form.append('bundle', file, file.name);
  const res = await apiFetchRaw(`${BACKUPS_ROOT}/upload`, { method: 'POST', body: form });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(body.message || body.error || `Upload failed (${res.status}).`);
  }
  return (await res.json()) as BackupEntry;
}
